import { Locales } from '@luminalearning/types';
import env from 'src/env';

// Add new ones in alphabetical order
const locales: Locales = {
    'cs-CZ': { localName: 'Čeština', englishName: 'Czech' },
    'da-DK': { localName: 'Dansk', englishName: 'Danish' },
    'en-CA': { localName: 'English (Canadian)', englishName: 'English (Canadian)' },
    'en-GB': { localName: 'English', englishName: 'English' },
    'fr-FR': { localName: 'Français', englishName: 'French' },
    'hu-HU': { localName: 'Magyar', englishName: 'Hungarian' },
    'id-ID': { localName: 'Bahasa Indonesia', englishName: 'Indonesian' },
    'ja-JP': { localName: '日本語', englishName: 'Japanese' },
    'lv-LV': { englishName: 'Latvian' },
    'nl-NL': { localName: 'Nederlands', englishName: 'Dutch' },
    'pl-PL': { localName: 'Polski', englishName: 'Polish' },
    'pt-PT': { localName: 'Português', englishName: 'Portuguese' },
    'sk-SK': { localName: 'Slovenčina', englishName: 'Slovakian' },
    'zh-CN': { localName: '中文(简体)', englishName: 'Chinese (simplified)' },
    'zh-TW': { localName: '中文(台灣)', englishName: 'Chinese (traditional)' },
};

const isLocaleValid = (locale: string) => Object.keys(locales).includes(locale);

const loadMessages = (locale: string) => {
    const translationFilename = isLocaleValid(locale) ? locale : env.defaultLocale;

    // eslint-disable-next-line import/no-dynamic-require, global-require
    const ui = require(`src/lang/ui/${translationFilename}.json`);
    const componentLanguageCode = translationFilename === 'en-CA' ? 'en_GB' : translationFilename.replace('-', '_');
    // eslint-disable-next-line import/no-dynamic-require, global-require
    const component = require(`@luminalearning/components/lang/${componentLanguageCode}.json`);

    return { ...ui, ...component };
};

const selectValidLocale = (locale: string | null) => {
    const selectedLocale = (locale === null || locale === '') ? navigator.language : locale;

    return isLocaleValid(selectedLocale) ? selectedLocale : env.defaultLocale;
};

// Flatten keys in translation json, react-intl doesn't support nested keys
// https://stackoverflow.com/questions/45783677/react-intl-accessing-nested-messages
const flattenTranslationKeys = ((messages: Record<string, unknown>, prefix = '') => {
    if (messages === null) return {};

    return Object.keys(messages).reduce((flatMessages, key) => {
        const value = messages[key];
        const prefixedKey = prefix ? `${prefix}.${key}` : key;

        const message = typeof value === 'string'
            ? { [prefixedKey]: value }
            : flattenTranslationKeys(value as Record<string, unknown>, prefixedKey);

        Object.assign(flatMessages, message);

        return flatMessages;
    }, {});
});

// We don't want to supply default messages for each string
// react-intl/formatjs doesn't handle it by default
// https://github.com/formatjs/formatjs/issues/557
const loadLocaleMessages = (locale: string) => {
    const validLocale = selectValidLocale(locale);
    const defaultMessages = flattenTranslationKeys(loadMessages(env.defaultLocale));

    return validLocale === env.defaultLocale
        ? defaultMessages
        : { ...defaultMessages, ...flattenTranslationKeys(loadMessages(validLocale)) };
};

export {
    locales,
    isLocaleValid,
    selectValidLocale,
    loadLocaleMessages,
};
